import React from "react";
import { Link } from "react-router-dom";
import "./LandingDisplay.css";
import { heroSrcSet } from "../../utils/heroSrcSet";

/**
 * /hdmi-display -- landing page for the HDMI touch display line.
 *
 * Static, prerendered marketing page (see prerender.js STATIC_ROUTES and
 * src/seo/pageMeta.js for the <head> tags and Product/FAQ JSON-LD). The
 * Firestore-driven category page at /category/special-displays/hdmi-displays
 * stays as the full filterable grid; this page exists so search engines and
 * Google Ads have a real, indexable page that answers "7 inch hdmi display",
 * "raspberry pi touch screen" and similar queries.
 *
 * Prices and part numbers mirror the live Tixaru catalog as of Oct 2026 --
 * if a slab changes there, update PRODUCTS below.
 */

export const HDMI_PRODUCTS = [
  {
    pn: "B-0525-QHB-0700-YX",
    name: "7 inch HDMI touch display",
    image: "/landing/hdmi-display-7-inch-b-0525.jpg",
    route: "/tft/special-displays/hdmi-displays/b-0525-qhb-0700-yx",
    datasheet:
      "https://tixaru.in/api/v1/public/catalog/attachments/cc66de16-390a-4e97-9c56-dec33978c180/file",
    specs: [
      ["Resolution", "1024 x 600"],
      ["Video input", "HDMI"],
      ["Touch", "Capacitive, USB"],
      ["Brightness", "500 nits"],
      ["Viewing angle", "All directions"],
      ["Active area", "154.1 x 85.9 mm"],
      ["Supply", "5 V"],
      ["Operating temp.", "-20 to 70 C"],
    ],
    priceFrom: 3700,
    priceSingle: 4500,
    slabs: [
      ["1 - 10", 4500],
      ["11 - 50", 4250],
      ["51 - 100", 4000],
      ["101 - 250", 3808],
      ["251 - 500", 3700],
    ],
    stockNote: "In stock, ships from Gurugram",
  },
  {
    pn: "B-0526-QHB-1010-YX",
    name: "10.1 inch HDMI touch display",
    image: "/landing/hdmi-display-10-inch-b-0526.jpg",
    route: "/tft/special-displays/hdmi-displays/b-0526-qhb-1010-yx",
    datasheet:
      "https://tixaru.in/api/v1/public/catalog/attachments/6a8318c1-2e33-457c-96e3-21391ea7915c/file",
    specs: [
      ["Resolution", "1024 x 600"],
      ["Video input", "HDMI"],
      ["Touch", "Capacitive, USB or I2C"],
      ["Brightness", "500 nits"],
      ["Viewing angle", "80 / 80 / 80 / 80"],
      ["Outline", "235 x 147 x 9.3 mm"],
      ["Supply", "4.5 to 5.5 V"],
      ["Operating temp.", "-20 to 70 C"],
    ],
    priceFrom: 4080,
    priceSingle: 5500,
    slabs: [
      ["1 - 9", 5500],
      ["10 - 49", 4590],
      ["50 - 249", 4250],
      ["250 - 499", 4080],
    ],
    stockNote: "In stock, lead time 5 to 7 weeks for volume",
  },
];

const inr = (n) => "₹" + n.toLocaleString("en-IN");

const ICONS = {
  plug: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22v-5" /><path d="M9 8V2" /><path d="M15 8V2" />
      <path d="M18 8v5a6 6 0 0 1-12 0V8z" />
    </svg>
  ),
  touch: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 11V6a2 2 0 0 0-4 0v2" /><path d="M14 10V4a2 2 0 0 0-4 0v2" />
      <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
      <path d="M18 11a2 2 0 1 1 4 0v3a8 8 0 0 1-8 8h-2a8 8 0 0 1-8-8 2 2 0 1 1 4 0" />
    </svg>
  ),
  sun: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1" />
    </svg>
  ),
  temp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
    </svg>
  ),
  box: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  factory: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
    </svg>
  ),
};

const FEATURES = [
  {
    icon: ICONS.plug,
    title: "Plug and play on any HDMI source",
    desc: "Connect the HDMI cable for video and the USB cable for touch and power. No driver, no configuration file, no kernel patch.",
  },
  {
    icon: ICONS.touch,
    title: "Capacitive touch over USB",
    desc: "The touch controller enumerates as a standard USB HID device, so Raspberry Pi OS, Ubuntu, Windows and Android recognise it out of the box.",
  },
  {
    icon: ICONS.sun,
    title: "500 nit IPS panel",
    desc: "Bright enough for a shop floor or a kiosk under room lighting, with full viewing angles so the image does not wash out off-axis.",
  },
  {
    icon: ICONS.temp,
    title: "Industrial temperature range",
    desc: "Rated for -20 to 70 C operation and tested for thermal cycling, damp heat, vibration, drop and ESD. See the reliability table in each datasheet.",
  },
  {
    icon: ICONS.box,
    title: "Mounting holes, not a consumer bezel",
    desc: "Designed to be screwed into an enclosure. Mechanical drawings are published for every part number so your panel cut-out is right first time.",
  },
  {
    icon: ICONS.factory,
    title: "Made in Gurugram, long availability",
    desc: "BIBA Electronics is an ISO 9001 display and EMS company. Part numbers stay in production for OEM programmes, with volume pricing published up front.",
  },
];

// "Works with" tiles. Each image is an AI-generated scene (no logos, no
// third-party photography) of the BIBA 7 inch HDMI display wired to that
// class of device with its HDMI and USB cables. Files under
// public/landing/compat.
const COMPAT = [
  {
    name: "Raspberry Pi 5 / 4 / 3",
    note: "Micro HDMI to HDMI, touch and power over one USB-A cable.",
    image: "/landing/compat/raspberry-pi.jpg",
    alt: "BIBA 7 inch HDMI touch display connected to a Raspberry Pi class single-board computer with HDMI and USB cables",
  },
  {
    name: "NVIDIA Jetson",
    note: "Orin Nano and Xavier dev kits: HDMI out, USB touch, 5 V from the carrier.",
    image: "/landing/compat/jetson.jpg",
    alt: "BIBA 7 inch HDMI touch display connected to a Jetson class AI developer kit with HDMI and USB cables",
  },
  {
    name: "Windows mini PCs and NUCs",
    note: "Windows 10 and 11 detect the panel and touch with no driver.",
    image: "/landing/compat/windows-pc.jpg",
    alt: "BIBA 7 inch HDMI touch display connected to a fanless Windows mini PC with HDMI and USB cables",
  },
  {
    name: "Industrial and panel PCs",
    note: "Fanless boxes running Linux or Windows in control cabinets.",
    image: "/landing/compat/industrial-pc.jpg",
    alt: "BIBA 7 inch HDMI touch display connected to a rugged DIN-rail industrial computer with HDMI and USB cables",
  },
  {
    name: "Rock Pi, Orange Pi, Banana Pi",
    note: "Any ARM single-board computer with an HDMI output.",
    image: "/landing/compat/sbc.jpg",
    alt: "BIBA 7 inch HDMI touch display connected to an ARM single-board computer with HDMI and USB cables",
  },
  {
    name: "Android and media boxes",
    note: "Android TV boxes, signage players and set-top units.",
    image: "/landing/compat/android-box.jpg",
    alt: "BIBA 7 inch HDMI touch display connected to an Android media box with HDMI and USB cables",
  },
];

// Side-by-side comparison of the two models (keep in sync with HDMI_PRODUCTS).
const COMPARE_ROWS = [
  ["Resolution", "1024 x 600", "1024 x 600"],
  ["Panel", "IPS TFT, all viewing angles", "TFT, 80 deg all sides"],
  ["Brightness", "500 nits standard, 1000 nits sunlight-readable option", "500 nits standard, 1000 nits sunlight-readable option"],
  ["Video input", "HDMI", "HDMI"],
  ["Touch", "Capacitive, USB", "Capacitive, USB or I2C"],
  ["Active area", "154.2 x 85.9 mm", "222.7 x 125.3 mm"],
  ["Outline", "166.2 x 102.0 x 15.3 mm", "235 x 147 x 9.3 mm"],
  ["Supply", "5 V over USB", "4.5 to 5.5 V, 2 A recommended"],
  ["Operating temperature", "-20 to 70 C", "-20 to 70 C"],
  ["Best for", "Raspberry Pi projects, handhelds, test fixtures, 3D printers", "Control panels, kiosks, medical carts, industrial PCs"],
];

// Interface comparison. Plain statements that answer "HDMI vs LVDS/RGB/MIPI/UART"
// questions the way an engineer would ask them.
const INTERFACES = [
  ["HDMI", "Any computer or SBC with an HDMI port", "Plug and play, OS renders the UI, USB touch", "Needs a host that runs an OS", "Raspberry Pi, Jetson, PCs, kiosks"],
  ["Smart UART", "Any MCU with a serial port", "Display renders its own UI, two wires", "UI fixed at design time in Dsync Studio", "Arduino, ESP32, STM32, PLC panels"],
  ["LVDS", "SoC or SBC with an LVDS transmitter", "Long cable runs, low EMI, 7 to 15 inch panels", "Needs a driver board or SoC support", "Industrial PCs, medical, automotive"],
  ["RGB (TTL)", "MCU or MPU with a parallel LCD controller", "Lowest cost bare panel", "40 to 50 wires, short cable, frame buffer on host", "Cost-driven appliances, 3 to 7 inch"],
  ["MIPI DSI", "Application processors and phone-class SoCs", "Few wires, high resolution", "Needs DSI on the SoC, driver bring-up", "Handhelds, tablets, premium HMIs"],
];

// Where these displays end up. Kept factual: segments BIBA actually ships into.
const APPLICATIONS = [
  { title: "Industrial HMI and machine panels", desc: "Operator screens on packaging, textile, printing and CNC machines, driven by an industrial PC or a Linux SBC in the control cabinet." },
  { title: "Test fixtures and lab instruments", desc: "Bench equipment that needs a touch UI without designing a display driver, often a Raspberry Pi with a Python or Qt front end." },
  { title: "Kiosks, vending and ticketing", desc: "Self-service units where the 10.1 inch panel gives room for a keypad and status messages, with the capacitive touch glass facing the public." },
  { title: "Medical and diagnostic devices", desc: "Monitoring carts, analysers and respiratory equipment where the display is mounted open-frame behind a sealed bezel." },
  { title: "EV chargers and energy monitors", desc: "Charger front panels and solar or battery monitoring units, usually with the 1000 nit option for outdoor cabinets." },
  { title: "Digital signage and smart retail", desc: "Shelf-edge and counter displays running Android or Linux media players over a single HDMI cable." },
];

const FAQ = [
  {
    q: "What is an HDMI touch display module?",
    a: "A bare TFT LCD panel with an HDMI driver board on the back, a capacitive touch sensor laminated on the front, and mounting holes so it can be fitted inside a product. The host computer sends video over HDMI and reads touch over USB. BIBA's modules are 7 inch and 10.1 inch, 1024 x 600 pixels, 500 nits, and are made in Gurugram, India.",
  },
  {
    q: "Does the 7 inch HDMI display work with a Raspberry Pi 5?",
    a: "Yes. Connect micro HDMI to HDMI for video and a USB cable to any USB port for touch and 5 V power. Raspberry Pi OS detects the 1024 x 600 panel over EDID and the touch controller as a USB HID device, with no config.txt changes.",
  },
  {
    q: "Does it work with Raspberry Pi 4, Pi 3, Zero 2 W and Compute Module 4?",
    a: "Yes. Any Raspberry Pi with an HDMI output works. The Pi 4 and Pi 5 use micro HDMI, the Pi 3 uses full size HDMI, the Zero 2 W uses mini HDMI, and CM4 carrier boards usually expose full size HDMI. Only the cable changes.",
  },
  {
    q: "Do I need a driver for the touch screen?",
    a: "No. Touch is a standard USB HID device, so it works on Raspberry Pi OS, Ubuntu, Debian, Windows 10 and 11, and Android without installing anything.",
  },
  {
    q: "Can I use it with a Windows laptop or mini PC as a second screen?",
    a: "Yes. Windows treats it as a 1024 x 600 monitor and a touch digitiser. Use Display settings to extend or duplicate the desktop and to rotate the screen.",
  },
  {
    q: "Does the touch screen work with NVIDIA Jetson and Ubuntu?",
    a: "Yes. JetPack and Ubuntu use the kernel's generic HID multitouch driver, so touch works out of the box on Jetson Orin Nano, Orin NX and Xavier developer kits.",
  },
  {
    q: "How is the display powered?",
    a: "Through its USB connector at 5 V. A Raspberry Pi USB port is enough for the 7 inch model. For the 10.1 inch model we recommend a dedicated 5 V, 2 A supply so the backlight does not share the Pi's USB budget.",
  },
  {
    q: "How do I rotate the screen to portrait?",
    a: "On Raspberry Pi OS Bookworm open Preferences, Screen Configuration, select the HDMI output and set the orientation; touch follows the rotation automatically under Wayland. On Windows use Display settings, Display orientation. On Ubuntu use Settings, Displays.",
  },
  {
    q: "What is the viewing angle and brightness?",
    a: "The 7 inch uses an IPS panel with full viewing angles in all directions. The 10.1 inch is rated 80 degrees on all four sides. Both are 500 nits as standard, with a 1000 nit sunlight-readable option for outdoor and high ambient light installations.",
  },
  {
    q: "What is the difference between the HDMI display and the Smart UART display?",
    a: "An HDMI display needs a computer or SBC that renders the screen. A Smart UART display has its own ESP32 processor and shows a UI you design in Dsync Studio, driven by simple serial commands from any microcontroller. If you have a Linux board, choose HDMI. If you have an Arduino, STM32 or PLC, choose UART.",
  },
  {
    q: "What is the minimum order quantity and price?",
    a: "You can order a single unit online. The 7 inch model is " + inr(4500) + " for 1 to 10 pieces and falls to " + inr(3700) + " at 251 pieces and above. The 10.1 inch model is " + inr(5500) + " single and " + inr(4080) + " at 250 and above. Prices exclude GST. GST invoices are issued for every order.",
  },
  {
    q: "Is a datasheet or mechanical drawing available?",
    a: "Yes. Each model has a downloadable datasheet with the mechanical drawing, pinout and reliability test table, linked from the comparison table above and from the product page. 2D drawings for panel cut-outs are provided on request for OEM programmes.",
  },
  {
    q: "Can BIBA customise the display for my product?",
    a: "Yes. We supply custom brightness, cover glass with printed borders, cable lengths and open-frame or enclosed variants for OEM volumes, and because BIBA is also an EMS company we can build the complete HMI board around the display in our Gurugram facility.",
  },
  {
    q: "Where are these displays made and what is the lead time?",
    a: "They are designed and assembled by BIBA Electronics in Gurugram, Haryana. The 7 inch ships from stock. The 10.1 inch ships from stock for small quantities, with a 5 to 7 week lead time for volume orders.",
  },
];

export { FAQ as HDMI_FAQ };

export default function HdmiDisplayPage() {
  return (
    <div className="ld-root">
      {/* Hero */}
      <section className="ld-hero">
        <div className="ld-wrap ld-hero-grid">
          <div>
            <span className="ld-eyebrow">HDMI Displays</span>
            <h1 className="ld-h1">
              7 inch and 10.1 inch <em>HDMI touch displays</em> for Raspberry Pi, Jetson and industrial PCs
            </h1>
            <p className="ld-lead">
              1024 x 600 IPS panels with capacitive touch over USB. Plug into any HDMI source, no drivers,
              rated for -20 to 70 C. Made in India, in stock, with volume pricing published.
            </p>
            <div className="ld-cta-row">
              <Link to={HDMI_PRODUCTS[0].route} className="ld-btn ld-btn-primary">
                Buy the 7 inch from {inr(HDMI_PRODUCTS[0].priceFrom)}
              </Link>
              <Link to="/contact" className="ld-btn ld-btn-secondary">
                Get a volume quote
              </Link>
            </div>
            <div className="ld-stats">
              <div className="ld-stat"><span className="ld-stat-val">1024 x 600</span><span className="ld-stat-lbl">Resolution</span></div>
              <div className="ld-stat"><span className="ld-stat-val">500 nits</span><span className="ld-stat-lbl">Brightness</span></div>
              <div className="ld-stat"><span className="ld-stat-val">USB touch</span><span className="ld-stat-lbl">Capacitive, no driver</span></div>
              <div className="ld-stat"><span className="ld-stat-val">-20 to 70 C</span><span className="ld-stat-lbl">Operating range</span></div>
            </div>
          </div>
          <div className="ld-hero-media">
            <img
              src={HDMI_PRODUCTS[0].image}
              srcSet={heroSrcSet(HDMI_PRODUCTS[0].image)}
              sizes="(max-width: 900px) 92vw, 600px"
              fetchPriority="high"
              alt="7 inch HDMI touch display with 1024x600 IPS panel and USB capacitive touch, part B-0525-QHB-0700-YX"
              width="1400"
              height="933"
            />
            <p className="ld-hero-caption">7 inch HDMI display, part number B-0525-QHB-0700-YX</p>
          </div>
        </div>
      </section>

      {/* Models: one comparison table carrying the product photos, specs,
          price breaks and buy links for both sizes */}
      <section className="ld-section ld-section-alt" id="models">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Two sizes, same 1024 x 600 panel family</h2>
            <p className="ld-sub">
              Same touch interface, same connectors, same temperature rating. Pick the 7 inch for handhelds,
              Raspberry Pi projects and test fixtures, the 10.1 inch for control panels, kiosks and industrial PCs.
            </p>
          </div>
          <div className="ld-table-wrap">
            <table className="ld-table ld-compare">
              <thead>
                <tr>
                  <th>Specification</th>
                  {HDMI_PRODUCTS.map((p) => (
                    <th key={p.pn}>
                      <img
                        className="ld-compare-img"
                        src={p.image}
                        alt={`${p.name}, part ${p.pn}`}
                        width="1400"
                        height="933"
                        loading="lazy"
                      />
                      <span className="ld-compare-title">{p.name}</span>
                      <span className="ld-compare-pn">Part no. {p.pn}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map(([label, a, b]) => (
                  <tr key={label}>
                    <th scope="row">{label}</th>
                    <td>{a}</td>
                    <td>{b}</td>
                  </tr>
                ))}
                <tr>
                  <th scope="row">Price breaks (ex GST)</th>
                  {HDMI_PRODUCTS.map((p) => (
                    <td key={p.pn}>
                      <ul className="ld-slab-list">
                        {p.slabs.map(([q, price]) => (
                          <li key={q}><span>{q} pcs</span><span>{inr(price)}</span></li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </tr>
                <tr>
                  <th scope="row">Stock</th>
                  {HDMI_PRODUCTS.map((p) => <td key={p.pn}>{p.stockNote}</td>)}
                </tr>
                <tr>
                  <th scope="row"></th>
                  {HDMI_PRODUCTS.map((p) => (
                    <td key={p.pn}>
                      <div className="ld-product-actions">
                        <Link to={p.route} className="ld-btn ld-btn-primary ld-btn-sm">View and buy</Link>
                        <a href={p.datasheet} className="ld-btn ld-btn-secondary ld-btn-sm" target="_blank" rel="noopener noreferrer">
                          Datasheet (PDF)
                        </a>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <div className="ld-crosslink">
            Need 1000 nits or a 10.4 inch 1024 x 768 panel? BIBA also builds sunlight-readable HDMI displays for
            outdoor and automotive use. <Link to="/tft-display">See the full TFT display range</Link> or{" "}
            <Link to="/contact">ask for a part number</Link>.
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="ld-section">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Built for products, not for desks</h2>
            <p className="ld-sub">
              Consumer portable monitors are cheap until you try to mount one inside a machine. These displays are
              bare modules with mounting holes, published drawings and an industrial temperature rating.
            </p>
          </div>
          <div className="ld-features">
            {FEATURES.map((f) => (
              <div className="ld-feature" key={f.title}>
                <div className="ld-feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Definition and interface comparison */}
      <section className="ld-section ld-section-alt">
        <div className="ld-wrap ld-two-col">
          <div className="ld-prose">
            <h2 className="ld-h2">What is an HDMI touch display module?</h2>
            <p>
              An HDMI touch display module is a TFT LCD panel with an HDMI driver board fitted on the back and a
              capacitive touch sensor laminated on the front. The host, such as a Raspberry Pi, an NVIDIA Jetson or
              a Windows PC, sends video over HDMI exactly as it would to a monitor, and reads finger positions over
              USB as a standard HID touch device. Nothing has to be installed on the host.
            </p>
            <p>
              The difference from a consumer monitor is mechanical. A module has no enclosure or stand. It has
              mounting holes, a published drawing, an industrial operating temperature range and a part number that
              stays in production, so an OEM can design an enclosure around it and keep buying the same part for
              years.
            </p>
            <p>
              BIBA Electronics makes two sizes, 7 inch and 10.1 inch, both 1024 x 600 pixels and 500 nits, with a
              1000 nit option. They are designed and assembled in Gurugram, India, with datasheets, GST invoicing
              and stock for single-unit orders.
            </p>
          </div>
          <aside className="ld-aside">
            <h3>At a glance</h3>
            <ul>
              <li>Sizes: 7 inch and 10.1 inch</li>
              <li>Resolution: 1024 x 600 pixels</li>
              <li>Brightness: 500 nits, 1000 nit option</li>
              <li>Video: HDMI, EDID auto-detect</li>
              <li>Touch: capacitive, USB HID, no driver</li>
              <li>Power: 5 V over USB</li>
              <li>Operating range: -20 to 70 C</li>
              <li>Mounting: four holes, open-frame</li>
              <li>Origin: designed and made in India</li>
            </ul>
          </aside>
        </div>
      </section>

      <section className="ld-section">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">HDMI vs UART, LVDS, RGB and MIPI: which display interface do you need?</h2>
            <p className="ld-sub">
              The right interface depends on what is driving the screen. This is how BIBA's engineers steer the
              choice when a customer sends a requirement.
            </p>
          </div>
          <div className="ld-table-wrap">
            <table className="ld-table">
              <thead>
                <tr>
                  <th>Interface</th>
                  <th>Host it needs</th>
                  <th>Strength</th>
                  <th>Trade-off</th>
                  <th>Typical use</th>
                </tr>
              </thead>
              <tbody>
                {INTERFACES.map(([name, host, plus, minus, use]) => (
                  <tr key={name}>
                    <th scope="row">{name}</th>
                    <td>{host}</td>
                    <td>{plus}</td>
                    <td>{minus}</td>
                    <td>{use}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="ld-crosslink">
            Need LVDS, RGB or MIPI instead? Those panels are on the <Link to="/tft-display">TFT display page</Link>.
            Driving the screen from a microcontroller? See the <Link to="/uart-display">Smart UART displays</Link>.
          </div>
        </div>
      </section>

      {/* Setup */}
      <section className="ld-section ld-section-alt">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Raspberry Pi setup in three steps</h2>
            <p className="ld-sub">The same steps apply to Jetson, Rock Pi, a Windows PC or any other HDMI source.</p>
          </div>
          <div className="ld-steps">
            <div className="ld-step">
              <span className="ld-step-num">1</span>
              <h3>Connect HDMI</h3>
              <p>Micro HDMI to HDMI on a Pi 4 or Pi 5, full-size HDMI on a Pi 3 or a PC. The panel reports its native 1024 x 600 mode over EDID.</p>
            </div>
            <div className="ld-step">
              <span className="ld-step-num">2</span>
              <h3>Connect USB</h3>
              <p>One USB cable carries 5 V power and the capacitive touch data. On a Pi it plugs into any USB-A port and shows up as a HID touchscreen.</p>
            </div>
            <div className="ld-step">
              <span className="ld-step-num">3</span>
              <h3>Boot</h3>
              <p>Raspberry Pi OS, Ubuntu and Windows pick the resolution and touch automatically. Rotate the screen in the display settings if you mount it portrait.</p>
            </div>
          </div>
          <div className="ld-crosslink">
            Full walkthrough with the cable for every Pi model:{" "}
            <Link to="/hdmi-display/raspberry-pi-setup">Raspberry Pi HDMI touch display setup guide</Link>. Using a
            Jetson, Windows or Ubuntu? See the{" "}
            <Link to="/hdmi-display/jetson-windows-ubuntu-setup">Jetson, Windows and Ubuntu setup guide</Link>.
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="ld-section ld-section-alt">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Where BIBA HDMI displays are used</h2>
            <p className="ld-sub">
              Six places the same two part numbers turn up, from a shop floor in Gurugram to a charging station on a
              highway.
            </p>
          </div>
          <div className="ld-features">
            {APPLICATIONS.map((a) => (
              <div className="ld-feature" key={a.title}>
                <h3>{a.title}</h3>
                <p>{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compatibility */}
      <section className="ld-section">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Works with</h2>
            <p className="ld-sub">
              Anything that outputs HDMI and has a USB host port. Our customers use the 7 inch display as a
              Raspberry Pi touch screen in test fixtures and 3D printers, and the 10.1 inch as the operator panel on
              industrial PCs, medical carts, vending machines and energy monitors.
            </p>
          </div>
          <div className="ld-compat-grid">
            {COMPAT.map((c) => (
              <div className="ld-compat-tile" key={c.name}>
                <div className="ld-compat-img">
                  <img src={c.image} alt={c.alt} width="800" height="600" loading="lazy" />
                </div>
                <div className="ld-compat-body">
                  <h3>{c.name}</h3>
                  <p>{c.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HDMI vs UART */}
      <section className="ld-section">
        <div className="ld-wrap ld-two-col">
          <div className="ld-prose">
            <h2 className="ld-h2">Also runs on</h2>
            <p>
              Raspberry Pi CM4 carrier boards, Intel NUCs, Ubuntu and Debian desktops, Android TV boxes, and any
              instrument or kiosk controller with an HDMI port. Windows 10 and 11, Raspberry Pi OS, Ubuntu and
              Android all treat the touch panel as a standard USB HID device.
            </p>
          </div>
          <aside className="ld-aside">
            <h3>HDMI or Smart UART?</h3>
            <ul>
              <li><strong>HDMI display:</strong> your Linux or Windows board renders the UI. Best with Raspberry Pi, Jetson and PCs.</li>
              <li><strong>Smart UART display:</strong> the display renders its own UI and talks to your MCU over two serial wires. Best with Arduino, ESP32, STM32 and PLC-class controllers.</li>
            </ul>
            <p style={{ marginTop: 12, marginBottom: 0 }}>
              <Link to="/uart-display" className="ld-btn ld-btn-secondary">See Smart UART displays</Link>
            </p>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="ld-section ld-section-alt">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Frequently asked questions</h2>
          </div>
          <div className="ld-faq">
            {FAQ.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ld-section">
        <div className="ld-wrap">
          <div className="ld-cta">
            <div>
              <h2>Order online or talk to an engineer</h2>
              <p>
                Single units ship from stock in Gurugram. For 100 pieces and above, or a custom cover glass, cable
                or enclosure, send us your requirement and we reply within one working day.
              </p>
            </div>
            <div className="ld-cta-actions">
              <Link to={HDMI_PRODUCTS[0].route} className="ld-btn ld-btn-primary">Buy 7 inch</Link>
              <Link to={HDMI_PRODUCTS[1].route} className="ld-btn ld-btn-primary">Buy 10.1 inch</Link>
              <Link to="/contact" className="ld-btn ld-btn-secondary">Request a quote</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
